import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

/**
 * Small dismissable banner that appears above the admin dashboard when
 * the org is at ≥80% of any plan limit (warn) or 100% (exceeded). Refreshes
 * every 60 seconds and respects a 24h localStorage snooze.
 */
export function UsageBanner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(() => isSnoozed());

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const d = await api.getBillingUsage();
        if (!cancelled) setData(d);
      } catch { /* tolerated — banner is optional UX */ }
    }
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (hidden || !data || (!data.warn && !data.exceeded)) return null;

  // Find the first warning to highlight.
  const offenders = Object.values(data.limits || {}).filter(
    (l) => l && (l.warn || l.exceeded),
  );
  if (offenders.length === 0) return null;
  const lead = offenders.find((l) => l.exceeded) || offenders[0];
  const isHard = !!lead.exceeded;

  function snooze() {
    try {
      localStorage.setItem('kw_usage_snooze_until', String(Date.now() + 24 * 3600 * 1000));
    } catch { /* private mode */ }
    setHidden(true);
  }

  return (
    <div
      role="status"
      data-testid="usage-banner"
      style={{
        ...wrap,
        background: isHard ? '#fde4e4' : '#fff4d6',
        color: isHard ? '#8a2929' : '#7a5a13',
        borderColor: isHard ? '#f5b8b8' : '#f1d98a',
      }}
    >
      <span style={{ fontSize: '1.1rem', flexShrink: 0 }} aria-hidden>
        {isHard ? '🚫' : '⚠️'}
      </span>
      <div style={{ flex: 1, minWidth: 200 }}>
        <strong>
          {isHard ? 'Plan limit reached' : 'Approaching your plan limit'}
        </strong>{' '}
        — You're using <strong>{lead.current}</strong> of {lead.allowed} {lead.label}
        {' '}on the <strong>{lead.plan_name}</strong> plan.
        {offenders.length > 1 && (
          <span style={{ opacity: 0.85 }}>
            {' '}
            ({offenders.length - 1} more limit{offenders.length - 1 === 1 ? '' : 's'} affected.)
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => navigate('/pricing')}
        data-testid="usage-banner-upgrade"
        style={ctaBtn}
      >Upgrade →</button>
      <button
        type="button"
        onClick={snooze}
        data-testid="usage-banner-dismiss"
        style={dismissBtn}
        aria-label="Dismiss for 24 hours"
      >×</button>
    </div>
  );
}

function isSnoozed() {
  try {
    const until = parseInt(localStorage.getItem('kw_usage_snooze_until') || '0', 10);
    return Number.isFinite(until) && Date.now() < until;
  } catch { return false; }
}

const wrap = {
  margin: '0 0 1rem 0',
  padding: '0.7rem 1rem',
  border: '1px solid',
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  fontSize: '0.92rem',
  lineHeight: 1.4,
};
const ctaBtn = {
  padding: '0.35rem 0.85rem',
  borderRadius: 999,
  border: 'none',
  background: 'hsl(189 53% 25%)',
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.82rem',
  cursor: 'pointer',
  flexShrink: 0,
};
const dismissBtn = {
  padding: '0.25rem 0.45rem',
  borderRadius: 6,
  border: 'none',
  background: 'transparent',
  color: 'currentColor',
  fontSize: '1.1rem',
  cursor: 'pointer',
  opacity: 0.6,
  flexShrink: 0,
};

export default UsageBanner;
